import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, User, Calendar, PlusCircle, ListPlus, ClipboardCheck, FileText, BarChart, AlertCircle, Edit } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SessionDetails } from '../types';
import { format, isAfter, isBefore, isToday, addDays } from 'date-fns';
import SessionPlanningReport from '../components/SessionPlanningReport';
import AttendanceReportModal from '../components/AttendanceReportModal';
import { getCoaches } from '../services/coachService';
import { getPlayers } from '../services/playerService';
import { getSessions } from '../services/sessionService';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAttendanceReportModal, setShowAttendanceReportModal] = useState(false);
  const [reportType, setReportType] = useState<'planning' | 'attendance'>('planning');
  const [showReportMenu, setShowReportMenu] = useState(false);

  const [totalPlayers, setTotalPlayers] = useState(0);
  const [totalCoaches, setTotalCoaches] = useState(0);
  const [upcomingSessions, setUpcomingSessions] = useState<SessionDetails[]>([]);
  const [recentSessions, setRecentSessions] = useState<SessionDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [coachesData, playersData, sessionsData] = await Promise.all([
        getCoaches(),
        getPlayers(),
        getSessions()
      ]);

      setTotalCoaches(coachesData.length);
      setTotalPlayers(playersData.length);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = addDays(today, 1);

      // Filter and sort sessions
      const upcoming = sessionsData
        .filter(session => isAfter(new Date(session.date), tomorrow) || isToday(new Date(session.date)))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      const recent = sessionsData
        .filter(session => isBefore(new Date(session.date), today) && !isToday(new Date(session.date)))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 3);

      setUpcomingSessions(upcoming);
      setRecentSessions(recent);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const canMarkAttendance = (session: SessionDetails): boolean => {
    return isToday(new Date(session.date)) && session.attendanceStatus !== 'marked';
  };

  const isUpcomingSession = (session: SessionDetails): boolean => {
    const sessionDate = new Date(session.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = addDays(today, 1);
    return isAfter(sessionDate, tomorrow) || isToday(sessionDate);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={loadDashboardData}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-gray-600">
            Welcome back, {user?.name}! Here's an overview of your academy.
          </p>
        </div>
        <div className="hidden md:block">
          <img src="/mbfc-logo.svg" alt="Maverick Blues FC Logo" className="h-24 w-24" />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Create Session */}
          <Link
            to="/sessions/new"
            className="flex items-center p-5 bg-[#002855] text-white rounded-lg shadow hover:bg-[#003c74] transition-colors group"
          >
            <div className="flex-shrink-0 bg-white bg-opacity-20 rounded-full p-3 mr-4">
              <PlusCircle className="h-6 w-6" />
            </div>
            <div>
              <p className="font-semibold text-lg">Create Session</p>
              <p className="text-blue-200 text-sm">Schedule a new training or match</p>
            </div>
            <div className="ml-auto opacity-50 group-hover:opacity-100 transition-opacity">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Mark Attendance — links to today's session if available */}
          {(() => {
            const todaySession = upcomingSessions.find(s => canMarkAttendance(s));
            const to = todaySession ? `/sessions/${todaySession.id}/attendance` : '/sessions';
            const subtitle = todaySession
              ? `Today: ${todaySession.type} – ${todaySession.location}`
              : 'No session today needing attendance';
            return (
              <Link
                to={to}
                className={`flex items-center p-5 rounded-lg shadow transition-colors group ${todaySession
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-white text-gray-400 border border-gray-200 cursor-default pointer-events-none'
                  }`}
              >
                <div className={`flex-shrink-0 rounded-full p-3 mr-4 ${todaySession ? 'bg-white bg-opacity-20' : 'bg-gray-100'}`}>
                  <ClipboardCheck className="h-6 w-6" />
                </div>
                <div>
                  <p className={`font-semibold text-lg ${todaySession ? 'text-white' : 'text-gray-500'}`}>
                    Mark Attendance
                  </p>
                  <p className={`text-sm ${todaySession ? 'text-green-100' : 'text-gray-400'}`}>
                    {subtitle}
                  </p>
                </div>
                {todaySession && (
                  <div className="ml-auto opacity-50 group-hover:opacity-100 transition-opacity">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </Link>
            );
          })()}
        </div>
      </div>

      {/* Upcoming sessions */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Upcoming Sessions</h2>
          <Link
            to="/sessions/new"
            className="flex items-center text-sm font-medium text-navy-800 hover:text-navy-900"
          >
            <PlusCircle size={16} className="mr-1" />
            <span className="hidden sm:inline">Add Session</span>
          </Link>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {upcomingSessions.length > 0 ? (
              upcomingSessions.slice(0, 3).map((session) => {
                const showMarkAttendance = canMarkAttendance(session);
                const isUpcoming = isUpcomingSession(session);

                return (
                  <li key={session.id}>
                    <div className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-navy-800 truncate">
                            {session.type} - {session.location}
                          </p>
                          <div className="ml-2 flex-shrink-0 flex">
                            <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {format(new Date(session.date), 'MMM d')} at {session.time}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <Users className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                              {session.squads.join(', ')}
                            </p>
                          </div>
                          <div className="mt-2 flex space-x-2 sm:mt-0">
                            {showMarkAttendance && (
                              <Link
                                to={`/sessions/${session.id}/attendance`}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <ClipboardCheck className="mr-1.5 h-4 w-4" />
                                <span className="hidden sm:inline">Mark Attendance</span>
                                <span className="sm:hidden">Attendance</span>
                              </Link>
                            )}
                            {isUpcoming && !session.locked ? (
                              <Link
                                to={`/sessions/${session.id}`}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                              >
                                <Edit className="mr-1.5 h-4 w-4 text-gray-400" />
                                <span>Edit</span>
                              </Link>
                            ) : (
                              <Link
                                to={`/sessions/${session.id}/summary`}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-navy-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                              >
                                View Summary
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-6 text-center text-gray-500">
                No upcoming sessions. <Link to="/sessions/new" className="text-navy-800 hover:text-navy-900">Create a new session</Link>.
              </li>
            )}
          </ul>
          {upcomingSessions.length > 3 && (
            <div className="bg-gray-50 px-4 py-4 text-center border-t border-gray-200">
              <Link to="/sessions" className="text-sm font-medium text-navy-600 hover:text-navy-800 flex items-center justify-center">
                View all upcoming sessions
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>


      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Stats cards */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-navy-800" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Players
                  </dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {totalPlayers}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link
                to="/players"
                className="font-medium text-navy-800 hover:text-navy-900"
              >
                View all players
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <User className="h-6 w-6 text-navy-800" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Coaches
                  </dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {totalCoaches}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link
                to="/coaches"
                className="font-medium text-navy-800 hover:text-navy-900"
              >
                View all coaches
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-6 w-6 text-navy-800" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Upcoming Sessions
                  </dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {upcomingSessions.length}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link
                to="/sessions"
                className="font-medium text-navy-800 hover:text-navy-900"
              >
                View all sessions
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart className="h-6 w-6 text-navy-800" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Reports
                  </dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      Generate
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="flex flex-col space-y-1">
              <button
                onClick={() => {
                  setReportType('planning');
                  setShowReportModal(true);
                }}
                className="text-sm font-medium text-navy-800 hover:text-navy-900 text-left"
              >
                Session Planning Report
              </button>
              <button
                onClick={() => {
                  setReportType('attendance');
                  setShowAttendanceReportModal(true);
                }}
                className="text-sm font-medium text-navy-800 hover:text-navy-900 text-left"
              >
                Attendance Report
              </button>
            </div>
          </div>
        </div>
      </div>



      {/* Recent sessions */}
      <div className="mt-8 mb-16">
        <h2 className="text-lg font-medium text-gray-900">Recent Sessions</h2>
        <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {recentSessions.length > 0 ? (
              recentSessions.map((session) => (
                <li key={session.id}>
                  <div className="block hover:bg-gray-50">
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-navy-800 truncate">
                          {session.type} - {session.location}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            {format(new Date(session.date), 'MMM d')}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            <Users className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                            {session.squads.join(', ')}
                          </p>
                        </div>
                        <div className="mt-2 flex space-x-2 sm:mt-0">
                          <Link
                            to={`/sessions/${session.id}/summary`}
                            className="text-navy-800 hover:text-navy-900"
                          >
                            View Summary
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <li className="px-4 py-6 text-center text-gray-500">
                No recent sessions.
              </li>
            )}
          </ul>
          {recentSessions.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 text-center">
              <Link to="/sessions" className="text-sm font-medium text-navy-800 hover:text-navy-900">
                View all sessions
              </Link>
            </div>
          )}
        </div>
      </div>

      {showReportModal && (
        <SessionPlanningReport onClose={() => setShowReportModal(false)} />
      )}

      {showAttendanceReportModal && (
        <AttendanceReportModal onClose={() => setShowAttendanceReportModal(false)} />
      )}
    </div>
  );
};

export default Dashboard;